import { Component, OnDestroy, computed, effect, inject, signal, untracked } from '@angular/core';
import { Auth } from '@angular/fire/auth';

import { AlarmService } from '../../../core/notifications/alarm.service';
import { SpeechService } from '../../../core/notifications/speech.service';
import { PermissionService } from '../../../core/permissions/permission.service';
import { FloorStore } from '../../../core/stores/floor.store';
import { OrdersStore } from '../../../core/stores/orders.store';
import { OrdersService } from '../../services/orders.service';
import { NvButton } from '../../ui/button/nv-button';
import { NvIcon } from '../../ui/icon/nv-icon';
import { NvToastService } from '../../ui/toast/nv-toast';
import {
  ReadyAlert,
  ServedAnnouncement,
  newServedAnnouncements,
  pendingReadyAlerts,
  readySpeech,
  servedSpeech,
} from '../../utils/kitchen-alerts.util';

/** How often an unacknowledged ready item is called out again. */
const REMIND_MS = 60_000;
const TICK_MS = 5_000;
/** Items named aloud in one go before the rest are summed up — same cap as OrderApprovalStrip. */
const MAX_SPOKEN_PER_BATCH = 3;

/**
 * What a manager hears from the chef and waiter screens, mounted in the shell
 * so it works on every console page.
 *
 * Ready: the tune, a spoken line and a card that stays until someone taps
 * "Got it" (or the waiter serves the item), repeating each minute until then.
 * Served: spoken only. Nothing to look at and nothing to acknowledge.
 *
 * Silent for anyone without manage_orders, and when the console is muted.
 */
@Component({
  selector: 'nv-kitchen-ready-alerts',
  imports: [NvButton, NvIcon],
  templateUrl: './kitchen-ready-alerts.html',
  styleUrl: './kitchen-ready-alerts.scss',
})
export class KitchenReadyAlerts implements OnDestroy {
  private readonly auth = inject(Auth);
  private readonly alarm = inject(AlarmService);
  private readonly speech = inject(SpeechService);
  private readonly permissions = inject(PermissionService);
  private readonly floor = inject(FloorStore);
  private readonly ordersStore = inject(OrdersStore);
  private readonly orders = inject(OrdersService);
  private readonly toasts = inject(NvToastService);

  protected readonly enabled = computed(() => this.permissions.can('manage_orders'));

  private readonly now = signal(Date.now());
  private readonly tickTimer = setInterval(() => this.now.set(Date.now()), TICK_MS);
  private readonly openedAtMs = Date.now();

  /** Hidden the moment "Got it" is tapped, ahead of the order's own update arriving. */
  private readonly acknowledging = signal<ReadonlySet<string>>(new Set());

  private readonly tableNumbers = computed(() => {
    const map = new Map<string, number>();
    for (const table of this.floor.tables()) map.set(table.id, table.number);
    return map;
  });

  protected readonly alerts = computed(() => {
    if (!this.enabled()) return [];
    const hidden = this.acknowledging();
    return pendingReadyAlerts(this.ordersStore.orders(), this.auth.currentUser?.uid ?? null, this.now()).filter(
      (alert) => !hidden.has(alert.key)
    );
  });

  private seenReady = new Set<string>();
  private lastCalledOutAt = 0;
  private readonly announcedServed = new Set<string>();
  /** Everything said goes through here, so a ready line and a served line never talk over each other. */
  private speaking: Promise<void> = Promise.resolve();

  constructor() {
    effect(() => {
      const alerts = this.alerts();
      this.now();
      untracked(() => this.callOutReady(alerts));
    });

    effect(() => {
      if (!this.enabled()) return;
      const orders = this.ordersStore.orders();
      untracked(() => {
        const fresh = newServedAnnouncements(orders, this.auth.currentUser?.uid ?? null, this.openedAtMs, this.announcedServed);
        if (fresh.length) this.announceServed(fresh);
      });
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.tickTimer);
  }

  protected tableLabel(tableId: string): string {
    const number = this.tableNumbers().get(tableId);
    return number !== undefined ? String(number) : tableId;
  }

  protected async acknowledge(alert: ReadyAlert): Promise<void> {
    this.acknowledging.update((keys) => new Set(keys).add(alert.key));
    try {
      await this.orders.acknowledgeLineReady(alert.orderId, alert.lineId);
    } catch {
      this.acknowledging.update((keys) => {
        const next = new Set(keys);
        next.delete(alert.key);
        return next;
      });
      this.toasts.open('Could not save that. Try again.', 'Dismiss', { duration: 4000 });
    }
  }

  private callOutReady(alerts: ReadyAlert[]): void {
    const fresh = alerts.filter((alert) => !this.seenReady.has(alert.key));
    this.seenReady = new Set(alerts.map((alert) => alert.key));
    if (alerts.length === 0) return;

    const due = Date.now() - this.lastCalledOutAt >= REMIND_MS;
    if (fresh.length === 0 && !due) return;
    this.lastCalledOutAt = Date.now();

    // Fresh items are named; a reminder names the oldest one still waiting.
    const named = fresh.length ? fresh : alerts.slice(0, 1);
    const lines = named.slice(0, MAX_SPOKEN_PER_BATCH).map((alert) => readySpeech(alert, this.tableLabel(alert.tableId)));
    const unnamed = (fresh.length ? fresh.length : alerts.length) - Math.min(named.length, MAX_SPOKEN_PER_BATCH);
    if (unnamed > 0) lines.push(`And ${unnamed} more ready.`);
    this.say(lines, true);
  }

  private announceServed(fresh: ServedAnnouncement[]): void {
    for (const item of fresh) this.announcedServed.add(item.key);
    const lines = fresh.slice(0, MAX_SPOKEN_PER_BATCH).map((item) => servedSpeech(item, this.tableLabel(item.tableId)));
    const unnamed = fresh.length - lines.length;
    if (unnamed > 0) lines.push(`And ${unnamed} more served.`);
    this.say(lines, false);
  }

  /** `withTune`: ready gets the alert tune under the words; served is the voice alone. */
  private say(lines: string[], withTune: boolean): void {
    this.speaking = this.speaking
      .then(async () => {
        if (this.alarm.muted()) return;
        if (withTune) this.alarm.playRound();
        try {
          if (this.speech.supported) {
            this.alarm.duck();
            try {
              for (const line of lines) await this.speech.announce(line);
            } finally {
              this.alarm.unduck();
            }
          }
        } finally {
          if (withTune) this.alarm.endRound();
        }
      })
      .catch((err) => console.error('KitchenReadyAlerts: announcement failed', err));
  }
}
